/*
 * Xboost "Estimated Delivery" block toggle.
 *
 * Shows exactly one Xboost block depending on state, in priority order:
 *   1. Custom colour  -> data-esd-custom-id     (wins over rush; ESD = 30 bd)
 *   2. SKU match      -> the matching rule's block (data-esd-sku-rules)
 *   3. Rushed         -> data-esd-rushed-id
 *   4. Otherwise      -> data-esd-nonrushed-id
 *
 * SKU match: the admin app ("Estimated Shipping Date" -> "SKU-matched delivery
 * estimate") writes [{block_id, skus}] to the hub.esd_sku_override shop
 * metafield. expivi-viewer.js publishes the configured SKU it builds on every
 * option change (data-expivi-sku on the configurator root + an `expivi:sku`
 * event); when that SKU is one of a rule's SKUs, the rule's block is shown.
 * Matching is by skuKey(): case-insensitive, segment order ignored, and an
 * entry may carry the product's main SKU first (CCX-...) as the Hub's sku_gen
 * does, though the browser-built SKU does not. Custom colour still wins: its
 * Expivi value has no SKU code, so a custom build could otherwise look like a
 * stock one with a segment missing.
 *
 * Signals:
 *   - Rushed: server-computed data-esd-initial-rushed (customer.is_rushed
 *     metafield, else company flag), OR a live "Rush Manufacturing" selection
 *     (line-item property "rush my order", the Expivi Rush checkbox being
 *     ticked, or the Expivi radio/toggle set to "Rush My Lead Time").
 *   - Custom colour: a colour field set to "Custom Color Match" (line-item
 *     property value, or the Expivi colour control's selected option).
 *
 * All blocks stay in the DOM so the Xboost app hydrates them; we only flip the
 * `hidden` attribute, re-evaluating live as the shopper changes options.
 */
(function () {
  'use strict';

  // --- Rush Manufacturing selected on this product? --------------------------
  function isItemRushed() {
    var rushed = false;

    // a) Cart-style line-item property: Rush Manufacturing == "rush my order".
    document.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      var name = (el.getAttribute('name') || '').toLowerCase();
      var value = (el.value || '').toLowerCase();
      if (name.indexOf('rush') !== -1 && value.indexOf('rush my order') !== -1) {
        rushed = true;
      }
    });

    // b) Expivi configurator "Rush Manufacturing" field. Two UI variants:
    //    - a checkbox: ticked == rushed.
    //    - a radio / toggle-button group (Standard Lead Time vs Rush My Lead
    //      Time): the CHECKED option's own label decides. A radio is always
    //      checked, so we must read the selected option text, not just "checked".
    document.querySelectorAll('.expivi-configurator__field').forEach(function (field) {
      var label = field.querySelector('.expivi-configurator__label, label');
      var text = ((label && label.textContent) || '').toLowerCase();
      if (text.indexOf('rush') === -1) return;

      var box = field.querySelector('input[type="checkbox"]');
      if (box) { if (box.checked) rushed = true; return; }

      var picked = field.querySelector('input[type="radio"]:checked');
      if (picked) {
        var optionEl = picked.closest('.expivi-options__option') || picked.parentElement;
        var optionText = ((optionEl && optionEl.textContent) || '').toLowerCase();
        if (optionText.indexOf('rush') !== -1) rushed = true;
      }
    });

    return rushed;
  }

  // --- Custom Color Match selected on this product? --------------------------
  // Label-independent: expivi-viewer.js re-renders the colour control for
  // logged-in B2B customers, so we don't rely on a field label containing
  // "color". We match on the stable Expivi option cid first (same for guests
  // and logged-in), then fall back to the selected text.
  function isCustomColor(cid) {
    var custom = false;

    // a) Cart-style line-item property value.
    document.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      var value = (el.value || '').toLowerCase();
      if (value.indexOf('custom color match') !== -1) custom = true;
    });

    // b) Any <select> whose current selection is Custom Color Match — by cid
    //    (bulletproof), else by the selected option's text (which may have a
    //    price appended, so we substring-match).
    document.querySelectorAll('select').forEach(function (sel) {
      if (sel.selectedIndex < 0) return;
      var opt = sel.options[sel.selectedIndex];
      if (!opt) return;
      if (cid && sel.value === cid) custom = true;
      if ((opt.textContent || '').toLowerCase().indexOf('custom color match') !== -1) custom = true;
    });

    // c) Custom listbox dropdowns (role="option" / aria-selected): a selected
    //    item labelled Custom Color Match, or one carrying the cid.
    document.querySelectorAll('[aria-selected="true"], .is-selected, .selected, .expivi-dropdown__option--selected').forEach(function (el) {
      var t = (el.textContent || '').toLowerCase();
      if (t.indexOf('custom color match') !== -1) custom = true;
      if (cid && (el.getAttribute('data-value') === cid || el.getAttribute('value') === cid)) custom = true;
    });

    // d) Expivi radio tiles / lists (.expivi-options--tiles/list/toggle). The
    //    colour pickers are radio groups now, so read the CHECKED option's own
    //    label. Matched by the option's label text (stable across products and
    //    their differing value cids), never the field label — a field named
    //    "Apron Custom Color Match Options" must not false-trigger.
    document.querySelectorAll('.expivi-options__option').forEach(function (opt) {
      var radio = opt.querySelector('input[type="radio"]');
      var picked = radio ? radio.checked : opt.classList.contains('expivi-options__option--selected');
      if (!picked) return;
      var lbl = opt.querySelector('.expivi-options__label');
      var t = ((lbl && (lbl.getAttribute('title') || lbl.textContent)) || '').toLowerCase();
      if (t.indexOf('custom color match') !== -1) custom = true;
    });

    return custom;
  }

  // --- Configured SKU matches an admin-listed SKU? ---------------------------

  // Upper-cased, de-duplicated, sorted segments. The Hub de-duplicates sku_gen
  // segments too (SkuGeneratorService::generate), and appends -RSH only once,
  // so set semantics are the faithful ones. Mirrored by
  // EsdSkuBlockController::matchKey() in the app.
  function skuKey(sku) {
    var seen = {};
    var out = [];
    String(sku || '').toUpperCase().split('-').forEach(function (seg) {
      seg = seg.trim();
      if (seg && !seen[seg]) { seen[seg] = true; out.push(seg); }
    });
    return out.sort().join('-');
  }

  // {skuKey: blockId}. First rule wins, as the app refuses one SKU in two rules.
  function buildSkuIndex(rules) {
    var index = {};
    (Array.isArray(rules) ? rules : []).forEach(function (rule) {
      if (!rule || !rule.block_id || !Array.isArray(rule.skus)) return;
      rule.skus.forEach(function (sku) {
        var key = skuKey(sku);
        if (key && !(key in index)) index[key] = String(rule.block_id);
      });
    });
    return index;
  }

  // What the Hub would put in front of the configured segments: the variant
  // SKU without VEN-, and the main SKU it collapses to (CCX-60 / CCX48 -> CCX).
  // The app's main_skus list is not visible here, so the main SKU is taken as
  // the first hyphen/underscore part and the leading run of letters.
  function skuPrefixes(variantSkus) {
    var out = [];
    String(variantSkus || '').split(',').forEach(function (raw) {
      var base = raw.trim().replace(/^VEN-/i, '');
      if (!base) return;
      var letters = base.match(/^[A-Za-z]+/);
      [base, base.split(/[-_]/)[0], letters && letters[0]].forEach(function (p) {
        if (p && out.indexOf(p) === -1) out.push(p);
      });
    });
    return out;
  }

  // The forms of the configured SKU that are looked up: as built, then the
  // full SKU expivi-viewer.js built with the app's real main SKU in front
  // (fullSku, e.g. CCX-3048-…), then with each guessed product prefix in front.
  function skuCandidates(generatedSku, prefixes, fullSku) {
    if (!generatedSku) return [];
    var candidates = [generatedSku];
    if (fullSku && fullSku !== generatedSku) candidates.push(fullSku);
    (prefixes || []).forEach(function (p) { candidates.push(p + '-' + generatedSku); });
    return candidates;
  }

  // The block id for this configured SKU, or null.
  function matchSkuBlock(generatedSku, index, prefixes, fullSku) {
    if (!generatedSku || !index) return null;
    var candidates = skuCandidates(generatedSku, prefixes, fullSku);
    for (var i = 0; i < candidates.length; i++) {
      var key = skuKey(candidates[i]);
      if (key && Object.prototype.hasOwnProperty.call(index, key)) return index[key];
    }
    return null;
  }

  // --- Console debug ----------------------------------------------------------
  // TEMP: true logs the generated SKU, the admin SKU list and the match result
  // to the browser console. Set back to false before launch; ?sku_debug=1
  // still turns it on then. Logged through pristineConsole(), because this
  // storefront silences window.console.
  var ESD_SKU_CONSOLE_DEBUG = true;

  function consoleDebugOn() {
    return ESD_SKU_CONSOLE_DEBUG ||
      (typeof location !== 'undefined' && /[?&]sku_debug=1(?:&|$)/.test(location.search));
  }

  // A console no page script has touched. Something on this storefront blanks
  // console.log (and evidently console.warn) on window.console; a hidden
  // iframe has its own, untouched console, and Chrome/Firefox/Safari all show
  // an iframe's output in the same DevTools console. Shared across both debug
  // files through window.__hoodslyDebugConsole so only one iframe is made.
  function pristineConsole() {
    if (typeof window === 'undefined') return null;
    if (window.__hoodslyDebugConsole) return window.__hoodslyDebugConsole;
    var c = window.console || null;
    try {
      var frame = document.createElement('iframe');
      frame.style.display = 'none';
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('data-hoodsly-debug-console', '');
      (document.body || document.documentElement).appendChild(frame);
      if (frame.contentWindow && frame.contentWindow.console) c = frame.contentWindow.console;
    } catch (e) { /* fall back to the page console */ }
    window.__hoodslyDebugConsole = c;
    return c;
  }

  // Latest debug state, readable by typing HoodslySkuDebug in the console even
  // if console output itself is blocked.
  function debugState(patch) {
    if (typeof window === 'undefined') return;
    var s = window.HoodslySkuDebug || (window.HoodslySkuDebug = {});
    for (var k in patch) {
      if (Object.prototype.hasOwnProperty.call(patch, k)) s[k] = patch[k];
    }
  }

  function debugLog() {
    if (!consoleDebugOn()) return;
    var c = pristineConsole();
    if (!c || !c.warn) return;
    try {
      // c.warn.apply(c, ['[ESD SKU]'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) { /* never let debugging break the toggle */ }
  }

  // The SKU expivi-viewer.js last built — the configurator this block sits in,
  // else the page's first one.
  function skuSource(root) {
    return (root.closest && root.closest('[data-expivi-sku]')) ||
      document.querySelector('[data-expivi-configurator][data-expivi-sku]');
  }

  function currentSku(root) {
    var cfg = skuSource(root);
    return cfg ? cfg.getAttribute('data-expivi-sku') || '' : '';
  }

  // The same SKU with the main SKU in front, as the viewer built it.
  function currentFullSku(root) {
    var cfg = skuSource(root);
    return cfg ? cfg.getAttribute('data-expivi-sku-full') || '' : '';
  }

  function initToggle(root) {
    if (root.dataset.esdToggleReady) return;
    root.dataset.esdToggleReady = '1';

    var customerRushed = root.dataset.esdInitialRushed === 'true';
    var customColorCid = root.dataset.esdCustomColorCid || '';

    var blocks = {
      nonrushed: root.querySelector('.xbEdd-block[data-id="' + root.dataset.esdNonrushedId + '"]'),
      rushed: root.querySelector('.xbEdd-block[data-id="' + root.dataset.esdRushedId + '"]'),
      custom: root.dataset.esdCustomId
        ? root.querySelector('.xbEdd-block[data-id="' + root.dataset.esdCustomId + '"]')
        : null
    };
    if (!blocks.nonrushed || !blocks.rushed) return;

    var skuRules = null;
    var skuIndex = null;
    try {
      skuRules = root.dataset.esdSkuRules ? JSON.parse(root.dataset.esdSkuRules) : null;
      skuIndex = skuRules ? buildSkuIndex(skuRules) : null;
    } catch (e) {
      skuIndex = null; // malformed metafield: behave as if none configured
      debugLog('admin SKU rules could not be read (malformed metafield):', root.dataset.esdSkuRules);
    }
    var skuPrefixList = skuPrefixes(root.dataset.esdSkuPrefixes);
    var skuBlocks = root.querySelectorAll('.xbEdd-block[data-esd-sku-block]');

    if (consoleDebugOn()) {
      debugState({
        esdToggle: 'running',
        adminRules: skuRules || [],
        productPrefixes: skuPrefixList,
        skuBlocksOnPage: Array.prototype.map.call(skuBlocks, function (el) { return el.getAttribute('data-id'); })
      });
    }
    if (!skuRules || !skuRules.length) {
      debugLog('no admin SKU rules on this page (none saved in the app, or not an Expivi product).');
    } else {
      skuRules.forEach(function (rule, i) {
        debugLog('admin rule ' + (i + 1) + ' — xBoost block id:', rule && rule.block_id,
          '| SKUs (' + ((rule && rule.skus) || []).length + '):', (rule && rule.skus) || []);
      });
      debugLog('product prefixes tried in front of the generated SKU:', skuPrefixList);
    }

    var last = null;
    var lastLogged = null;

    function apply() {
      var state;
      var sku = currentSku(root);
      var fullSku = currentFullSku(root);
      var skuBlockId = skuIndex && skuBlocks.length
        ? matchSkuBlock(sku, skuIndex, skuPrefixList, fullSku)
        : null;
      if (blocks.custom && isCustomColor(customColorCid)) {
        state = 'custom';
      } else if (skuBlockId) {
        state = 'sku:' + skuBlockId;
      } else if (customerRushed || isItemRushed()) {
        state = 'rushed';
      } else {
        state = 'nonrushed';
      }

      // Once per change of SKU or shown block, not on every poll.
      var logKey = sku + '|' + fullSku + '|' + state;
      if (logKey !== lastLogged) {
        lastLogged = logKey;
        var shownId = state.indexOf('sku:') === 0 ? state.slice(4)
          : state === 'custom' ? root.dataset.esdCustomId
          : state === 'rushed' ? root.dataset.esdRushedId
          : root.dataset.esdNonrushedId;
        debugLog(
          'generated SKU:', fullSku || sku || '(none yet)',
          '| matched:', skuBlockId ? 'YES' : 'NO',
          '| matched xBoost id:', skuBlockId || '-',
          '| showing:', state.indexOf('sku:') === 0 ? 'sku match' : state, '(' + shownId + ')',
          skuBlockId && state === 'custom' ? '| custom colour wins over the SKU match' : ''
        );
        if (sku) {
          debugLog('looked up as:', skuCandidates(sku, skuPrefixList, fullSku).map(skuKey));
        }
        if (consoleDebugOn()) {
          debugState({
            matchedSku: fullSku || sku,
            matched: !!skuBlockId,
            matchedXboostId: skuBlockId || null,
            showing: state,
            showingXboostId: shownId
          });
        }
      }

      if (state === last) return;
      last = state;

      if (blocks.nonrushed) blocks.nonrushed.hidden = state !== 'nonrushed';
      if (blocks.rushed) blocks.rushed.hidden = state !== 'rushed';
      if (blocks.custom) blocks.custom.hidden = state !== 'custom';
      skuBlocks.forEach(function (el) {
        el.hidden = state !== 'sku:' + el.getAttribute('data-id');
      });
    }

    function schedule() {
      clearTimeout(apply._t);
      apply._t = setTimeout(apply, 80);
    }

    document.addEventListener('change', schedule, true);
    document.addEventListener('input', schedule, true);
    document.addEventListener('options:price', schedule);
    document.addEventListener('variant:change', schedule);
    document.addEventListener('expivi:sku', schedule);
    // Safety net for Expivi setting values without firing events.
    setInterval(apply, 750);

    apply();
  }

  function boot() {
    document.querySelectorAll('.xbEdd-esd').forEach(initToggle);
  }

  // Node (esd-xboost-toggle.test.js) gets the pure matching helpers; the
  // browser gets the toggle.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      skuKey: skuKey,
      skuCandidates: skuCandidates,
      buildSkuIndex: buildSkuIndex,
      skuPrefixes: skuPrefixes,
      matchSkuBlock: matchSkuBlock
    };
  }
  if (typeof document === 'undefined') return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();